import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { fetchContactsPathAware } from "@/lib/contacts/contactsRouterPathAware";
import type { ContactResult } from "@/lib/contacts/types";
import { inputClass } from "./ManufacturingOrderIntakeShared";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type IntakePickedContact = {
  id: string;
  name: string;
  email: string;
  phone: string;
};

function asPicked(row: ContactResult): IntakePickedContact | null {
  if (row.source !== "external") return null;
  const id = typeof row.sourceId === "string" ? row.sourceId.trim() : "";
  if (!id) return null;
  return {
    id,
    name: (row.name ?? "").trim(),
    email: (row.personEmail ?? row.email ?? "").trim(),
    phone: (row.phone ?? "").trim(),
  };
}

export function IntakeEndCustomerContactField(props: {
  selectedLabel: string;
  disabled?: boolean;
  onPick: (hit: IntakePickedContact) => void;
  onClear: () => void;
}) {
  const auth = useAuth();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<IntakePickedContact[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setHits([]);
      return;
    }
    if (UUID_RE.test(term)) {
      setHits([]);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(() => {
      setBusy(true);
      void fetchContactsPathAware({
        q: term,
        limit: 8,
        organizationId: auth.activeOrganizationId,
        locationId: auth.activeLocationId,
      }).then((r) => {
        if (cancelled) return;
        setBusy(false);
        if (!r.ok) {
          setHits([]);
          return;
        }
        setHits(r.results.map(asPicked).filter((x): x is IntakePickedContact => Boolean(x)));
      });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [q, auth.activeOrganizationId, auth.activeLocationId]);

  function applyTypedUuid() {
    const term = q.trim();
    if (!UUID_RE.test(term)) return;
    props.onPick({ id: term, name: "", email: "", phone: "" });
    setQ("");
    setHits([]);
  }

  return (
    <div className="relative">
      {props.selectedLabel ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700">
          <span className="min-w-0 truncate">{props.selectedLabel}</span>
          <button
            type="button"
            className="shrink-0 text-xs text-indigo-700 underline dark:text-indigo-300"
            disabled={props.disabled}
            onClick={props.onClear}
          >
            Change
          </button>
        </div>
      ) : (
        <input
          className={inputClass}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onBlur={() => applyTypedUuid()}
          placeholder="Search directory by name, email, or phone"
          disabled={props.disabled}
        />
      )}
      {!props.selectedLabel && hits.length > 0 ? (
        <ul className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white text-sm shadow dark:border-zinc-700 dark:bg-zinc-900">
          {hits.map((h) => (
            <li key={h.id}>
              <button
                type="button"
                className="block w-full px-2 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-zinc-800"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  props.onPick(h);
                  setQ("");
                  setHits([]);
                }}
              >
                <span className="font-medium">{h.name || h.id.slice(0, 8)}</span>
                {h.email || h.phone ? (
                  <span className="mt-0.5 block text-[11px] text-gray-500">
                    {[h.email, h.phone].filter(Boolean).join(" · ")}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {!props.selectedLabel && busy ? (
        <p className="mt-0.5 text-[11px] text-gray-500">Searching…</p>
      ) : null}
    </div>
  );
}
